import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Camera, Loader2, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, Cpu, Truck, Gauge, ClipboardCheck, Wifi, FileDown, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (p) => `${BACKEND_URL}/api/files/${p}`;
const inp = "w-full bg-secondary/40 border border-border rounded-md px-3 py-3 text-base outline-none focus:border-[#17A2B8]";

const UNIT_TYPES = [
  { v: "voiture", l: "Voiture", metric: "distance", road: true },
  { v: "utilitaire", l: "Utilitaire / VUL", metric: "distance", road: true },
  { v: "camion", l: "Camion", metric: "distance", road: true },
  { v: "engin", l: "Engin de chantier", metric: "engine_hours", road: false },
  { v: "machine", l: "Machine", metric: "engine_hours", road: false },
  { v: "materiel", l: "Matériel", metric: "engine_hours", road: false },
  { v: "bateau", l: "Bateau", metric: "distance", road: false },
];
const MODELS = ["FMC130", "FMC125", "FMC150", "CR300B", "CV200"];
const isRoad = (t) => (UNIT_TYPES.find((x) => x.v === t) || {}).road;
const STEPS = ["Société", "Boîtier + SIM", "Véhicule", "Compteur", "Récapitulatif", "Finalisation", "Contrôle"];

const Conf = ({ v }) => (v == null ? null : <span className="text-[11px] text-muted-foreground ml-2">confiance {Math.round(v * 100)}%</span>);

export default function InstallWizard({ open, onClose, companies, onFinished, resumeId }) {
  const [step, setStep] = useState(1);
  const [installId, setInstallId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState("");
  const [f, setF] = useState({ company_id: "", imei: "", iccid: "", model: "FMC130", sim_number: "",
    unit_name: "", unit_type: "camion", plate: "", park_number: "", serial: "", internal_id: "",
    usage_metric: "distance", counter_value: "", result: "ok", notes: "" });
  const [photos, setPhotos] = useState([]);   // [{path,url,name,kind}]
  const [check, setCheck] = useState(null);
  const [simMsg, setSimMsg] = useState("");
  const [tele, setTele] = useState(null);
  const [finalizeErr, setFinalizeErr] = useState(false);
  const [finalStatus, setFinalStatus] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const reset = () => { setStep(1); setInstallId(null); setF({ company_id: "", imei: "", iccid: "", model: "FMC130", sim_number: "", unit_name: "", unit_type: "camion", plate: "", park_number: "", serial: "", internal_id: "", usage_metric: "distance", counter_value: "", result: "ok", notes: "" }); setPhotos([]); setCheck(null); setSimMsg(""); setTele(null); setFinalizeErr(false); setFinalStatus(null); };

  // Reprise d'un brouillon existant
  const loadDraft = useCallback(async (id) => {
    try {
      const r = await api.get(`/installations/${id}`);
      const ins = r.data.install || {};
      setInstallId(id);
      setF((s) => ({ ...s, company_id: r.data.company_id || "", imei: ins.imei || "", iccid: ins.iccid || "",
        model: ins.model || "FMC130", sim_number: ins.sim_number || "", unit_name: ins.unit_name || "",
        unit_type: ins.unit_type || "camion", plate: ins.plate || "", park_number: ins.park_number || "",
        serial: ins.serial || "", internal_id: ins.internal_id || "", usage_metric: ins.usage_metric || "distance",
        counter_value: ins.counter_value ?? "", result: ins.result || "ok", notes: r.data.notes || "" }));
      setPhotos(r.data.photos || []);
      setStep(ins.imei ? 2 : 1);
    } catch { toast.error("Reprise impossible"); }
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    if (resumeId) loadDraft(resumeId);
  }, [open, resumeId, loadDraft]);

  // Autosave serveur du brouillon (jamais de photo base64 en local ; localStorage = id + étape)
  const persist = useCallback(async (extraInstall = {}, extraTop = {}) => {
    if (!installId) return;
    const install = { imei: f.imei, iccid: f.iccid, model: f.model, sim_number: f.sim_number, unit_name: f.unit_name,
      unit_type: f.unit_type, plate: f.plate, park_number: f.park_number, serial: f.serial, internal_id: f.internal_id,
      usage_metric: f.usage_metric, counter_value: f.counter_value === "" ? null : Number(f.counter_value), ...extraInstall };
    try { await api.put(`/installations/${installId}`, { status: "en_cours", install, photos, notes: f.notes, ...extraTop }); } catch { /* réseau instable : on garde l'étape */ }
  }, [installId, f, photos]);

  useEffect(() => { if (installId) localStorage.setItem("install_wip", JSON.stringify({ id: installId, step })); }, [installId, step]);

  const startDraft = async () => {
    if (!f.company_id) { toast.error("Choisissez une société"); return; }
    setBusy(true);
    try {
      if (!installId) { const r = await api.post("/installations", { company_id: f.company_id }); setInstallId(r.data.id); }
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    setBusy(false);
  };

  // Upload générique (front/back/installed) via /upload
  const uploadPhoto = async (file, kind) => {
    setOcrBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd);
      setPhotos((p) => [...p.filter((x) => x.kind !== kind), { ...r.data, kind }]);
      toast.success("Photo ajoutée");
    } catch { toast.error("Échec de l'envoi de la photo"); }
    setOcrBusy("");
  };

  const runOcr = async (endpoint, file, kind, form) => {
    setOcrBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (form) Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const r = await api.post(endpoint, fd);
      if (r.data.file) setPhotos((p) => [...p.filter((x) => x.kind !== kind), { ...r.data.file, kind }]);
      return r.data;
    } catch {
      toast.info("Lecture automatique impossible. Saisissez la valeur manuellement.");
      return null;
    } finally { setOcrBusy(""); }
  };

  const onDeviceSimPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const d = await runOcr("/installations/ocr/device-sim", file, "device_sim");
    if (d) {
      if (d.imei) set("imei", d.imei);
      if (d.iccid) set("iccid", d.iccid);
      if (d.model) set("model", d.model);
      setF((s) => ({ ...s, _ocrConf: d.confidence }));
      if (d.iccid) doSimLookup(d.iccid);
    }
  };
  const doSimLookup = async (iccid) => {
    if (!iccid) return;
    try {
      const r = await api.get(`/installations/sim-lookup?iccid=${encodeURIComponent(iccid)}`);
      if (r.data.found) { set("sim_number", r.data.sim_number); setSimMsg(""); }
      else setSimMsg("Numéro d'appel non trouvé");
    } catch { setSimMsg("Numéro d'appel non trouvé"); }
  };
  const doCheck = async () => {
    if (!f.imei) { toast.error("IMEI requis"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/installations/check-device?imei=${encodeURIComponent(f.imei)}&iccid=${encodeURIComponent(f.iccid || "")}`);
      setCheck(r.data);
      await persist();
    } catch { toast.error("Vérification impossible"); }
    setBusy(false);
  };

  const onMeterPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const d = await runOcr("/installations/ocr/meter", file, "meter", { metric: f.usage_metric });
    if (d && d.value) set("counter_value", String(d.value));
    if (d) setF((s) => ({ ...s, _meterConf: d.confidence }));
  };
  const onPlatePhoto = async (e, kind) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const d = await runOcr("/installations/ocr/plate", file, kind);
    if (d && d.plate && isRoad(f.unit_type) && !f.plate) set("plate", d.plate);
    if (d && d.plate) setF((s) => ({ ...s, _plateSug: d.plate, _plateConf: d.confidence }));
  };

  const selectType = (t) => {
    const def = UNIT_TYPES.find((x) => x.v === t);
    setF((s) => ({ ...s, unit_type: t, usage_metric: def ? def.metric : s.usage_metric }));
  };

  const identityOk = () => {
    if (!f.unit_name.trim()) return false;
    if (isRoad(f.unit_type)) return !!f.plate.trim();
    return !!(f.plate.trim() || f.park_number.trim() || f.serial.trim() || f.internal_id.trim());
  };

  const finalize = async () => {
    setBusy(true); setFinalizeErr(false);
    const body = { imei: f.imei, iccid: f.iccid || null, sim_number: f.sim_number || null, model: f.model,
      unit_name: f.unit_name, unit_type: f.unit_type, plate: f.plate || null, park_number: f.park_number || null,
      serial: f.serial || null, internal_id: f.internal_id || null, usage_metric: f.usage_metric,
      counter_value: f.counter_value === "" ? null : Number(f.counter_value), result: f.result, notes: f.notes };
    try {
      const r = await api.post(`/installations/${installId}/finalize`, body);
      setFinalStatus(r.data.status);
      localStorage.removeItem("install_wip");
      setStep(7);
    } catch (e) {
      // réseau/timeout : vérifier si déjà finalisée avant tout retry
      try {
        const chk = await api.get(`/installations/${installId}`);
        if (chk.data.install?.finalized) { setFinalStatus(chk.data.status); setStep(7); setBusy(false); return; }
      } catch { /* ignore */ }
      setFinalizeErr(true);
      toast.error(e?.response?.data?.detail || "Installation enregistrée partiellement — reprendre");
    }
    setBusy(false);
  };

  // Contrôle télématique : polling ciblé 5 s, stoppé à la sortie de l'étape
  useEffect(() => {
    if (step !== 7 || !f.imei) return;
    let alive = true;
    const tick = async () => {
      try { const r = await api.get(`/installations/telemetry?imei=${encodeURIComponent(f.imei)}`); if (alive) setTele(r.data); } catch { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(iv); };
  }, [step, f.imei]);

  if (!open) return null;
  const dev = UNIT_TYPES.find((x) => x.v === f.unit_type);
  const co = companies.find((c) => c.id === f.company_id);
  const photoOf = (k) => photos.find((p) => p.kind === k);

  const PhotoBtn = ({ kind, label, onChange, testid }) => {
    const ph = photoOf(kind);
    return (
      <div>
        <label className="flex items-center justify-center gap-2 w-full min-h-[52px] rounded-md border-2 border-dashed border-border cursor-pointer hover:bg-secondary/40 text-sm font-semibold" data-testid={testid}>
          {ocrBusy === kind ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5 text-[#17A2B8]" />} {label}
          <input type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden" onChange={onChange} />
        </label>
        {ph && (
          <div className="flex items-center gap-2 mt-2">
            <img src={fileUrl(ph.path)} alt="" className="w-14 h-14 rounded object-cover border border-border" data-testid={`${testid}-thumb`} />
            <button onClick={() => setPhotos((p) => p.filter((x) => x.kind !== kind))} data-testid={`${testid}-remove`} className="text-xs text-destructive">Supprimer</button>
          </div>
        )}
      </div>
    );
  };

  const next = async (to) => { await persist(); setStep(to); };
  const canFinalize = f.imei && f.model && f.unit_name && identityOk() && !(check?.imei_assigned);

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-background w-full sm:max-w-md sm:rounded-xl rounded-t-2xl max-h-[94vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="install-wizard">
        <div className="sticky top-0 bg-background border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <div>
            <p className="text-[11px] text-muted-foreground">Installation · étape {step} / 7</p>
            <h2 className="text-base font-bold">{STEPS[step - 1]}</h2>
          </div>
          <button onClick={onClose} data-testid="wizard-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="h-1 bg-secondary"><div className="h-1 bg-[#F5821F] transition-all" style={{ width: `${(step / 7) * 100}%` }} /></div>

        <div className="p-4 space-y-4">
          {step === 1 && (
            <>
              <label className="label-mono block">Société</label>
              <select className={inp} value={f.company_id} onChange={(e) => set("company_id", e.target.value)} data-testid="wiz-company">
                <option value="">— Sélectionner —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p className="text-xs text-muted-foreground">Seules vos sociétés autorisées sont listées.</p>
            </>
          )}

          {step === 2 && (
            <>
              <PhotoBtn kind="device_sim" label="📷 Photographier boîtier + SIM" onChange={onDeviceSimPhoto} testid="wiz-photo-devicesim" />
              <div>
                <label className="label-mono block mb-1">IMEI détecté <Conf v={f._ocrConf} /></label>
                <input className={inp} inputMode="numeric" value={f.imei} onChange={(e) => { set("imei", e.target.value); setCheck(null); }} placeholder="IMEI" data-testid="wiz-imei" />
              </div>
              <div>
                <label className="label-mono block mb-1">ICCID détecté</label>
                <input className={inp} inputMode="numeric" value={f.iccid} onChange={(e) => set("iccid", e.target.value)} onBlur={() => doSimLookup(f.iccid)} placeholder="ICCID" data-testid="wiz-iccid" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Modèle</label>
                  <select className={inp} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="wiz-model">
                    {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1">Numéro d'appel</label>
                  <input className={inp} value={f.sim_number} onChange={(e) => set("sim_number", e.target.value)} placeholder="N° SIM" data-testid="wiz-sim" />
                </div>
              </div>
              {simMsg && <p className="text-xs text-[#F5821F]" data-testid="wiz-sim-msg">{simMsg}</p>}
              <button onClick={doCheck} disabled={busy || !f.imei} data-testid="wiz-check" className="w-full border border-border rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />} Vérifier le boîtier</button>
              {check && (
                <div className="rounded-md border p-3 text-sm" data-testid="wiz-check-result"
                  style={check.imei_assigned ? { borderColor: "#EF4444", background: "#EF444414" } : { borderColor: "#10B981", background: "#10B98114" }}>
                  {check.imei_assigned ? <p className="text-[#EF4444] font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Boîtier déjà affecté à une unité — création bloquée</p>
                    : check.imei_exists ? <p className="text-[#F5821F] font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Boîtier déjà connu (non affecté) — il sera réutilisé</p>
                    : <p className="text-[#10B981] font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> IMEI disponible</p>}
                  {check.iccid_exists && <p className="text-[#F5821F] text-xs mt-1">⚠ ICCID déjà associé à un boîtier</p>}
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <label className="label-mono block">Type d'unité</label>
              <div className="grid grid-cols-2 gap-2" data-testid="wiz-types">
                {UNIT_TYPES.map((t) => (
                  <button key={t.v} onClick={() => selectType(t.v)} data-testid={`wiz-type-${t.v}`}
                    className={`text-sm font-semibold rounded-md px-2 py-2.5 border ${f.unit_type === t.v ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border hover:bg-secondary/50"}`}>{t.l}</button>
                ))}
              </div>
              <div>
                <label className="label-mono block mb-1">Nom de l'unité</label>
                <input className={inp} value={f.unit_name} onChange={(e) => set("unit_name", e.target.value)} placeholder="Ex : Camion 12 / Pelle 3" data-testid="wiz-name" />
              </div>
              {isRoad(f.unit_type) ? (
                <div>
                  <label className="label-mono block mb-1">Plaque d'immatriculation</label>
                  <input className={inp} value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="Plaque" data-testid="wiz-plate" />
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Sans plaque : renseignez au moins un identifiant.</p>
                  <input className={inp} value={f.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="Plaque (optionnelle)" data-testid="wiz-plate" />
                  <input className={inp} value={f.park_number} onChange={(e) => set("park_number", e.target.value)} placeholder="N° de parc" data-testid="wiz-park" />
                  <input className={inp} value={f.serial} onChange={(e) => set("serial", e.target.value)} placeholder="N° de série" data-testid="wiz-serial" />
                  <input className={inp} value={f.internal_id} onChange={(e) => set("internal_id", e.target.value)} placeholder="Identifiant interne" data-testid="wiz-internal" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <PhotoBtn kind="front" label="📷 Avant" onChange={(e) => onPlatePhoto(e, "front")} testid="wiz-photo-front" />
                <PhotoBtn kind="back" label="📷 Arrière" onChange={(e) => onPlatePhoto(e, "back")} testid="wiz-photo-back" />
              </div>
              {f._plateSug && <p className="text-xs text-muted-foreground">Plaque détectée : <b>{f._plateSug}</b><Conf v={f._plateConf} /></p>}
              <div>
                <label className="label-mono block mb-1">Mode de suivi</label>
                <div className="flex gap-2">
                  <button onClick={() => set("usage_metric", "distance")} data-testid="wiz-metric-distance" className={`flex-1 rounded-md py-2 text-sm font-semibold border ${f.usage_metric === "distance" ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border"}`}>Kilométrage</button>
                  <button onClick={() => set("usage_metric", "engine_hours")} data-testid="wiz-metric-hours" className={`flex-1 rounded-md py-2 text-sm font-semibold border ${f.usage_metric === "engine_hours" ? "bg-[#17A2B8] text-white border-[#17A2B8]" : "border-border"}`}>Heures moteur</button>
                </div>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <PhotoBtn kind="meter" label={`📷 Photographier le compteur (${f.usage_metric === "engine_hours" ? "heures" : "km"})`} onChange={onMeterPhoto} testid="wiz-photo-meter" />
              <div>
                <label className="label-mono block mb-1">Valeur détectée ({f.usage_metric === "engine_hours" ? "h" : "km"}) <Conf v={f._meterConf} /></label>
                <input className={inp} inputMode="decimal" value={f.counter_value} onChange={(e) => set("counter_value", e.target.value)} placeholder={f.usage_metric === "engine_hours" ? "Ex : 3218.7" : "Ex : 82540"} data-testid="wiz-counter" />
              </div>
              <p className="text-xs text-muted-foreground">La valeur validée devient le compteur effectif initial (ancrage).</p>
            </>
          )}

          {step === 5 && (
            <div className="space-y-2 text-sm" data-testid="wiz-recap">
              {[["Société", co?.name, 1], ["Type", dev?.l, 3], ["Nom", f.unit_name, 3],
                ["Plaque / ID", f.plate || f.park_number || f.serial || f.internal_id || "—", 3],
                ["IMEI", f.imei, 2], ["ICCID", f.iccid || "—", 2], ["N° d'appel", f.sim_number || "—", 2],
                ["Modèle", f.model, 2], ["Suivi", f.usage_metric === "engine_hours" ? "Heures moteur" : "Kilométrage", 3],
                ["Compteur initial", f.counter_value !== "" ? `${f.counter_value} ${f.usage_metric === "engine_hours" ? "h" : "km"}` : "—", 4]].map(([k, v, toStep]) => (
                <div key={k} className="flex items-center justify-between border-b border-border py-1.5">
                  <span className="text-muted-foreground text-xs">{k}</span>
                  <span className="flex items-center gap-2"><b className="text-right">{v}</b><button onClick={() => setStep(toStep)} data-testid={`recap-edit-${toStep}`} className="text-[11px] text-[#17A2B8]">Modifier</button></span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-1">Photos : {photos.length} jointe(s)</p>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-3">
              <PhotoBtn kind="device_installed" label="📷 Boîtier installé (optionnel)" onChange={(e) => uploadPhoto(e.target.files?.[0], "device_installed")} testid="wiz-photo-installed" />
              <textarea className={inp} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notes (optionnel)" data-testid="wiz-notes" />
              {finalizeErr && <div className="rounded-md border border-[#EF4444] bg-[#EF444414] p-3 text-sm text-[#EF4444]" data-testid="wiz-finalize-err"><AlertTriangle className="w-4 h-4 inline mr-1" /> Installation enregistrée partiellement — cliquez à nouveau pour reprendre.</div>}
              <button onClick={finalize} disabled={busy || !canFinalize} data-testid="wiz-finalize" className="w-full bg-[#F5821F] text-white font-bold py-3.5 rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ClipboardCheck className="w-5 h-5" />} {finalizeErr ? "Reprendre la finalisation" : "Terminer l'installation"}
              </button>
              {!canFinalize && <p className="text-xs text-[#F5821F] text-center">{check?.imei_assigned ? "Boîtier déjà affecté." : "Complétez IMEI, nom et plaque/identifiant."}</p>}
            </div>
          )}

          {step === 7 && (
            <div className="space-y-3" data-testid="wiz-control">
              {(() => {
                const c = tele?.checks || {};
                const allOk = c.gprs_ok && c.gps_ok && c.power_ok;
                return (
                  <div className={`rounded-md border p-3 ${allOk ? "border-[#10B981] bg-[#10B98114]" : "border-[#F5821F] bg-[#F5821F14]"}`} data-testid="wiz-diag-banner">
                    <p className={`font-bold flex items-center gap-2 ${allOk ? "text-[#10B981]" : "text-[#F5821F]"}`}>
                      {allOk ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />} {allOk ? "Installation opérationnelle" : "À contrôler"}</p>
                    {!allOk && !c.gps_ok && c.gprs_ok && <p className="text-xs mt-1">Le boîtier communique mais aucune position GPS récente n'a été reçue.</p>}
                  </div>
                );
              })()}
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[["GPRS", tele?.checks?.gprs_ok, tele?.gprs_age_s != null ? `${tele.gprs_age_s}s` : "—"],
                  ["GPS", tele?.checks?.gps_ok, tele?.gps_age_s != null ? `${tele.gps_age_s}s` : "—"],
                  ["Satellites", (tele?.satellites || 0) >= 4, tele?.satellites ?? "—"],
                  ["GSM", tele?.gsm_signal != null, tele?.gsm_signal != null ? `${tele.gsm_signal}/5` : "—"],
                  ["Alimentation", tele?.checks?.power_ok, tele?.external_voltage_v != null ? `${tele.external_voltage_v} V` : "—"],
                  ["Contact", null, tele?.ignition == null ? "—" : (tele.ignition ? "ON" : "OFF")],
                  ["CAN", tele?.can_detected, tele?.can_detected ? "Détecté" : "Non détecté"]].map(([k, ok, val]) => (
                  <div key={k} className="flex items-center justify-between border border-border rounded-md px-3 py-2" data-testid={`diag-${k}`}>
                    <span className="text-muted-foreground text-xs">{k}</span>
                    <span className="flex items-center gap-1 font-semibold">{ok === true ? <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" /> : ok === false ? <AlertTriangle className="w-3.5 h-3.5 text-[#F5821F]" /> : null} {val}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><RefreshCw className="w-3 h-3 animate-spin" /> Actualisation toutes les 5 s…</div>
              <a href={`${BACKEND_URL}/api/interventions/${installId}/pdf`} target="_blank" rel="noreferrer" data-testid="wiz-pdf" className="w-full border border-border rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Voir le rapport PDF</a>
              <button onClick={() => { onFinished && onFinished(); onClose(); }} data-testid="wiz-done" className="w-full bg-[#10B981] text-white font-bold py-3 rounded-md">Terminer</button>
            </div>
          )}
        </div>

        {/* Navigation */}
        {step < 7 && (
          <div className="sticky bottom-0 bg-background border-t border-border p-3 flex gap-2">
            {step > 1 && step !== 6 && <button onClick={() => setStep(step - 1)} data-testid="wiz-back" className="px-4 py-3 rounded-md border border-border text-sm font-semibold flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Retour</button>}
            {step === 1 && <button onClick={startDraft} disabled={busy || !f.company_id} data-testid="wiz-next" className="flex-1 bg-[#17A2B8] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-1 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continuer"} <ChevronRight className="w-4 h-4" /></button>}
            {step === 2 && <button onClick={() => next(3)} disabled={!f.imei || check?.imei_assigned} data-testid="wiz-next" className="flex-1 bg-[#17A2B8] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-1 disabled:opacity-50">Continuer <ChevronRight className="w-4 h-4" /></button>}
            {step === 3 && <button onClick={() => next(4)} disabled={!identityOk()} data-testid="wiz-next" className="flex-1 bg-[#17A2B8] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-1 disabled:opacity-50">Continuer <ChevronRight className="w-4 h-4" /></button>}
            {step === 4 && <button onClick={() => next(5)} data-testid="wiz-next" className="flex-1 bg-[#17A2B8] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-1">Continuer <ChevronRight className="w-4 h-4" /></button>}
            {step === 5 && <button onClick={() => next(6)} data-testid="wiz-next" className="flex-1 bg-[#F5821F] text-white font-semibold py-3 rounded-md flex items-center justify-center gap-1">Finaliser <ChevronRight className="w-4 h-4" /></button>}
          </div>
        )}
      </div>
    </div>
  );
}
